import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getNewsList } from '../../../api';
import { getCurrentLangText } from '../../../lib';
import getFormatMessage from '../../../locales/get-format-message';
import { NewsModule } from '../../../model';
import mainStyles from '../../studio/main-wrapper/index.module.scss';
import { INews } from '../../../model/api-modules';
import notificationIcon from '../../../../src/assets/svg/notification-filled.svg';
import closeIcon from '../../../../src/assets/svg/notice-close-icon.svg';
import styles from './index.module.scss';
import {
  getObject,
  setObject,
} from '../../../lib/local-storage/interface-base';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store/reducers';

const LAST_CLOSE_NOTICE_KEY = 'ingame_console_last_close_notice_id';

interface IProps {
  className?: string;
  onDisplayChange?: (show: boolean) => void;
}

export const GlobalNotification = (props: IProps) => {
  const location = useLocation();
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const [news, setNews] = useState<INews>();

  useEffect(() => {
    adjustStyle(!!news);
  }, [location]);

  useEffect(() => {
    const links = document.querySelectorAll(
      `.${styles.globalNotification} a`
    ) as NodeListOf<HTMLElement>;
    links.forEach((link) => {
      link.setAttribute('target', '_blank');
    });
    adjustStyle(!!news);
    window.addEventListener('resize', adjustStyleHandler);

    return () => {
      window.removeEventListener('resize', adjustStyleHandler);
    };
  }, [news]);

  const getPageType = () => {
    const isDownPage = /^\/download/i.test(location.pathname);
    const isDocPage = /^\/doc/i.test(location.pathname);
    const isAdminPage = /^\/admin/i.test(location.pathname);
    const isPriceCalculatorPage = /^\/price-calculator/i.test(
      location.pathname
    );

    if (isAdminPage || isPriceCalculatorPage) {
      return;
    }

    if (isDownPage) {
      return NewsModule.UpgradeDownloadCenter;
    }
    if (isDocPage) {
      return NewsModule.UpgradeDocumentation;
    }

    return NewsModule.UpgradeDevPortal;
  };
  const pageType = getPageType();

  useEffect(() => {
    init();
  }, [pageType, userInfo]);

  const init = async () => {
    if (!userInfo) {
      return;
    }
    const pageType = getPageType();

    if (!pageType) {
      return;
    }

    const res = await getNewsList({
      Module: String(pageType),
    });
    const newsList = res?.list;
    const firstItem = newsList?.[0];

    const lastCloseId = getLatestCloseId();

    if (lastCloseId === firstItem?.id) {
      return;
    }

    if (firstItem) {
      setNews(firstItem);
      props.onDisplayChange?.(true);
    }
  };

  const setStyle = (
    selector: string,
    styles: { top?: string; marginTop?: string; height?: string } = {}
  ) => {
    const maxCount = 5;
    let currentTime = 0;

    const applyStyle = () => {
      currentTime = currentTime + 1;
      if (currentTime > maxCount) {
        return;
      }
      const element = document.querySelector(selector) as
        | HTMLElement
        | undefined;
      if (!element) {
        setTimeout(applyStyle, 100);
        return;
      }

      Object.assign(element.style, styles);
    };

    applyStyle();
  };

  const adjustStyle = useCallback(
    (hasNews?: boolean) => {
      const setTop = (top: number) => {
        const sidebarElement = document.querySelector('.gc-sidebar') as
          | HTMLElement
          | undefined;
        const navElement = document.querySelector('.portal-nav-box') as
          | HTMLElement
          | undefined;

        const mainWrapperElement = document.querySelector(
          `.${mainStyles.pageContentMain}`
        ) as HTMLElement | undefined;

        const consoleContentElement = document.querySelector(
          '.gc-console-content'
        ) as HTMLElement | undefined;

        const docContentElement = document.querySelector(
          '.c-main-content-wapper'
        ) as HTMLElement | undefined;

        const docHomeElement = document.querySelector('.document-home') as
          | HTMLElement
          | undefined;

        const needFitElements = [sidebarElement, navElement];

        const contentElements = [
          mainWrapperElement,
          consoleContentElement,
          docContentElement,
          docHomeElement,
        ];
        needFitElements.forEach((ele) => {
          if (!ele) {
            return;
          }

          // eslint-disable-next-line no-param-reassign
          ele.style.top = `${top}px`;
        });

        contentElements.forEach((ele) => {
          if (!ele) {
            return;
          }

          // eslint-disable-next-line no-param-reassign
          ele.style.marginTop = `${top - 48}px`;
        });

        setStyle('.download-center-headline', {
          marginTop: `${top - 48}px`,
        });

        setStyle('.gc-console .services-box', {
          height: `calc(100vh - ${top - 48}px)`,
        });
      };

      if (!hasNews) {
        setTop(48);
        setStyle('.header-comp-container  ', { top: '0px' });
        return;
      }

      const noticeElement = document.querySelector(
        `.${styles.globalNotification}`
      ) as HTMLElement | undefined;
      if (!noticeElement) {
        return;
      }
      const noticeHeight = noticeElement.offsetHeight;
      setTop(noticeHeight + 48);
      setStyle('.header-comp-container  ', { top: `${noticeHeight}px` });
    },
    [news]
  );

  const adjustStyleHandler = useCallback(
    debounce(() => {
      adjustStyle(!!news);
    }, 200),
    [news]
  );

  const saveCloseHistory = () => {
    const pageType = getPageType();
    if (!news || !pageType) {
      return;
    }

    const histories =
      getObject<Record<string, number>>(LAST_CLOSE_NOTICE_KEY) || {};
    setObject(LAST_CLOSE_NOTICE_KEY, { ...histories, [pageType]: news.id });
  };

  const getLatestCloseId = () => {
    const pageType = getPageType();
    if (!pageType) {
      return;
    }

    const histories =
      getObject<Record<string, number>>(LAST_CLOSE_NOTICE_KEY) || {};
    return histories[pageType];
  };

  const close = () => {
    saveCloseHistory();
    setNews(undefined);
    props.onDisplayChange?.(false);
  };

  if (!news) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.globalNotification,
        'global-notice-console',
        props.className
      )}
    >
      <div className={styles.notificationItem}>
        <span className={styles.title}>
          <img src={notificationIcon} width={16} height={16} />
          {getCurrentLangText(news?.Title) ||
            `${getFormatMessage('ingame_upgrade_notification')}`}
        </span>
        <span
          className={styles.content}
          dangerouslySetInnerHTML={{
            __html: getCurrentLangText(news?.Content),
          }}
        ></span>
      </div>

      <img className={styles.closeIcon} src={closeIcon} onClick={close} />
    </div>
  );
};
